import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, Info, Loader2, UserPlus } from 'lucide-react';

import { useAuth } from '@/lib/AuthContext';
import { useToast } from '@/components/ui/use-toast';
import { useLanguage } from '@/components/LanguageContext';
import { apiMessage } from '@/api/apiMessage';
import { buildLoginUrl, readRedirectParam, sanitizeRedirect } from '@/lib/authRedirect';
import { getCtaId } from '@/lib/ctaTracking';
import { pushEvent } from '@/lib/ga';
import httpClient from '@/api/httpClient';
import { countries } from '@/lib/phoneCountries';
import './landingV2.css';

const LOGO_SRC = '/landing-images/logo.png';
const INPUT_CLS =
  'mt-1.5 w-full h-11 px-3.5 rounded-xl border border-line bg-mist text-sm text-ink focus:outline-none focus:border-[#feae2c] focus:bg-white transition-colors';

// "CONFIRMAR CONTRASEÑA" -> "Confirmar contraseña" (sentence case, multi-idioma).
const sentenceCase = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s);

/**
 * Resuelve a dónde navegar después del login/registro.
 * Si el redirect viene de un escaneo QR (/redirect/?id=...), va directamente
 * a la página de configuración del producto según su tipo.
 * Con un redirect explícito de otro tipo (viene de Login, de alguien que
 * intentaba abrir una página concreta) se respeta. Sin redirect, a Mi Empresa.
 */
// Sin redirect se aterriza en Mi Empresa y no en `/`: con sesión, `/` es
// Dispositivos (`mainPage`), y el que se registra desde la landing o /pricing
// todavía no tiene ninguno. Solo el flujo QR acaba en Dispositivos, y llega
// por su propio camino (`RegisterQR` navega allí al guardar).
const POST_SIGNUP_DEFAULT = '/MyBusiness';

export async function resolvePostAuthRedirect(rawRedirect) {
  // Igual que en Login.jsx: nada llega al `navigate()` sin pasar por aquí.
  const redirectUrl = sanitizeRedirect(rawRedirect);
  if (!redirectUrl || redirectUrl === '/') return POST_SIGNUP_DEFAULT;
  try {
    const url = new URL(redirectUrl, window.location.origin);
    const qrId = url.searchParams.get('id');
    if (url.pathname.includes('/redirect') && qrId) {
      const { data } = await httpClient.get(`/qr/check/${qrId}/`);
      const platform = data?.product?.platform || '';
      if (platform === 'todo_en_1' || platform === 'tipo_todo_en_1') {
        return `/LinkTreeConfig?qrId=${qrId}`;
      }
      return `/RegisterQR?qrId=${qrId}`;
    }
  } catch {
    // Si falla la llamada al API, fallback al redirect original
  }
  return redirectUrl;
}

const Register = () => {
  const [formData, setFormData] = useState({
    email: '',
    confirmEmail: '',
    password: '',
    confirmPassword: '',
    username: '',
    phone: '',
    countryPrefix: '+34',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { t, language } = useLanguage();

  // Mismo criterio que Login.jsx: `redirect` canónico, `next` como alias, ya
  // validado. Así el destino sobrevive el salto login ↔ registro.
  const redirectTarget = readRedirectParam(searchParams);
  const loginHref = buildLoginUrl(redirectTarget);

  useEffect(() => {
    calculateStrength(formData.password);
  }, [formData.password]);

  const calculateStrength = (pass) => {
    let strength = 0;
    if (pass.length >= 8) strength += 25;
    if (/[A-Z]/.test(pass)) strength += 25;
    if (/[0-9]/.test(pass)) strength += 25;
    if (/[^A-Za-z0-9]/.test(pass)) strength += 25;
    setPasswordStrength(strength);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.id]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.email !== formData.confirmEmail) {
      toast({ variant: 'destructive', title: t('error'), description: t('emails_dont_match') });
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast({ variant: 'destructive', title: t('error'), description: t('passwords_dont_match') });
      return;
    }

    // Phone is required. Basic client-side sanity (digits, length); the backend
    // does the authoritative per-prefix validation with libphonenumber.
    const nationalNumber = formData.phone.replace(/[\s()\-.]/g, '');
    if (!/^\d{4,15}$/.test(nationalNumber)) {
      toast({ variant: 'destructive', title: t('error'), description: t('invalid_phone') || 'Introduce un número de teléfono válido.' });
      return;
    }

    setIsLoading(true);

    try {
      const { authService } = await import('@/api/authService');
      await authService.register({
        username: formData.username,
        email: formData.email,
        password: formData.password,
        phone: `${formData.countryPrefix}${nationalNumber}`,
        phonePrefix: formData.countryPrefix,
        preferredLanguage: language,
        ctaId: getCtaId(),
      });
      pushEvent('sign_up', { method: 'email' });

      toast({ title: t('register_success_title'), description: t('register_success_desc') });

      const loginResult = await login(formData.email, formData.password);
      if (loginResult.success) {
        const target = await resolvePostAuthRedirect(redirectTarget);
        navigate(target);
      } else {
        // Si el auto-login posterior al alta falla, el destino tampoco se
        // pierde: viaja al formulario de login.
        navigate(buildLoginUrl(redirectTarget));
      }
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('register_error_title'),
        description: apiMessage(error, t, t('register_error_fallback')),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getStrengthColor = () => {
    if (passwordStrength <= 25) return 'bg-red-500';
    if (passwordStrength <= 50) return 'bg-orange-500';
    if (passwordStrength <= 75) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  return (
    <div className="landing-v2 min-h-screen w-full overflow-x-hidden flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
            <div className="text-center">
              <Link to="/" className="inline-flex"><img src={LOGO_SRC} alt="Tapstar" className="h-8 w-auto mx-auto" /></Link>
              <h1 className="text-2xl sm:text-3xl font-extrabold tracking-[-0.02em] mt-6">{t('register_title')}</h1>
            </div>

            <form onSubmit={handleSubmit} className="mt-8 bg-white rounded-2xl border border-line soft-shadow-lg p-6 sm:p-7 space-y-4">
              {/* Usuario / Nombre del negocio */}
              <label className="block">
                <span className="text-[13px] font-semibold text-ink">{t('username_label_up')}</span>
                <input id="username" type="text" placeholder={t('username')} value={formData.username} onChange={handleChange} className={INPUT_CLS} required />
              </label>

              {/* Email */}
              <label className="block">
                <span className="text-[13px] font-semibold text-ink">{t('email_label_up')}</span>
                <input id="email" type="email" placeholder="tu@negocio.com" value={formData.email} onChange={handleChange} className={INPUT_CLS} required />
              </label>

              {/* Confirmar email */}
              <label className="block">
                <span className="text-[13px] font-semibold text-ink">{t('confirm_email_label_up')}</span>
                <input id="confirmEmail" type="email" placeholder="tu@negocio.com" value={formData.confirmEmail} onChange={handleChange} className={INPUT_CLS} required />
              </label>

              {/* Teléfono + prefijo */}
              <div className="block">
                <span className="text-[13px] font-semibold text-ink">{t('phone_label_up')}</span>
                <div className="mt-1.5 flex gap-2">
                  <select
                    value={formData.countryPrefix}
                    onChange={(e) => setFormData({ ...formData, countryPrefix: e.target.value })}
                    className="w-28 h-11 px-2.5 rounded-xl border border-line bg-mist text-sm text-ink focus:outline-none focus:border-[#feae2c] focus:bg-white transition-colors cursor-pointer"
                  >
                    {countries.map((co) => (
                      <option key={co.code} value={co.prefix}>{co.flag} {co.prefix}</option>
                    ))}
                  </select>
                  <input id="phone" type="tel" placeholder={t('phone')} value={formData.phone} onChange={handleChange} className="flex-1 min-w-0 h-11 px-3.5 rounded-xl border border-line bg-mist text-sm text-ink focus:outline-none focus:border-[#feae2c] focus:bg-white transition-colors" required />
                </div>
              </div>

              {/* Contraseña + fuerza */}
              <div className="block">
                <span className="text-[13px] font-semibold text-ink">{t('password_label_up')}</span>
                <div className="relative">
                  <input id="password" type={showPassword ? 'text' : 'password'} placeholder={t('password_label')} value={formData.password} onChange={handleChange} className={`${INPUT_CLS} pr-11`} required />
                  <button type="button" onClick={() => setShowPassword((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-ink transition-colors" aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}>
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <div className="mt-2 h-1 bg-line rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all ${getStrengthColor()}`} style={{ width: `${passwordStrength}%` }} />
                </div>
                <p className="mt-1.5 text-[11px] text-slate-500 flex items-start gap-1"><Info className="w-3 h-3 mt-0.5 shrink-0" /> {t('password_hint')}</p>
              </div>

              {/* Confirmar contraseña */}
              <div className="block">
                <span className="text-[13px] font-semibold text-ink">{t('confirm_password_label_up')}</span>
                <div className="relative">
                  <input id="confirmPassword" type={showConfirmPassword ? 'text' : 'password'} placeholder={sentenceCase(t('confirm_password_label_up'))} value={formData.confirmPassword} onChange={handleChange} className={`${INPUT_CLS} pr-11`} required />
                  <button type="button" onClick={() => setShowConfirmPassword((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-ink transition-colors" aria-label={showConfirmPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}>
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Términos + privacidad */}
              <div className="space-y-2.5 pt-1">
                <label className="flex items-start gap-2.5 cursor-pointer">
                  <input type="checkbox" checked={acceptedTerms} onChange={(e) => setAcceptedTerms(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-line accent-[#feae2c] cursor-pointer" required />
                  <span className="text-xs text-slate-500 leading-tight">{t('accept_terms_pre')} <Link to="/terms" target="_blank" rel="noopener noreferrer" className="text-ink font-semibold hover:underline">{t('terms_of_service')}</Link></span>
                </label>
                <label className="flex items-start gap-2.5 cursor-pointer">
                  <input type="checkbox" checked={acceptedPrivacy} onChange={(e) => setAcceptedPrivacy(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-line accent-[#feae2c] cursor-pointer" required />
                  <span className="text-xs text-slate-500 leading-tight">{t('accept_privacy_pre')} <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="text-ink font-semibold hover:underline">{t('privacy_policy')}</Link></span>
                </label>
              </div>

              <button
                type="submit"
                disabled={isLoading || !acceptedTerms || !acceptedPrivacy}
                className="inline-flex items-center justify-center gap-1.5 w-full h-12 rounded-full font-semibold text-black bg-[#feae2c] hover:bg-[#e89d1a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><UserPlus className="w-5 h-5" /> {t('create_account_btn')}</>}
              </button>
            </form>

            <p className="mt-5 text-center text-[14px] text-slate-500">
              {t('already_have_account')} <Link to={loginHref} className="font-semibold text-amberdeep hover:underline">{t('login_button')}</Link>
            </p>
      </div>
    </div>
  );
};

export default Register;
